// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook module for managing purchase order lists with filtering capabilities.
 * Provides a convenient interface for fetching and filtering purchase order data.
 */
import { locationCompanyTypeSchema } from '@/constants/location-company-type';
import { sortDirectionSchema } from '@/constants/sort';
import { getPurchaseOrders } from '@/purchase-order/api/get-purchase-orders';
import {
  PurchaseOrderFilter,
  poPhaseSchema,
  poSubstatusSchema,
} from '@/purchase-order/constants/status';
import { purchaseOrderListQueryKey } from '@/purchase-order/query-keys';
import { searchTermSchema } from '@/utilities/search-term';
import { useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';

/**
 * Configuration options for the usePurchaseOrders hook.
 */
interface UsePurchaseOrdersOptions {
  filter?: PurchaseOrderFilter;
}

/**
 * React hook for fetching and managing purchase order lists with filtering.
 * Uses React Query's suspense mode for data fetching.
 *
 * Features:
 * - Automatic data fetching and caching via React Query
 * - Filtering support via PurchaseOrderFilter options
 * - Suspense mode for loading states
 *
 * @param options - Configuration object for the hook
 *
 * @returns Object containing:
 * - purchaseOrders: Filtered array of purchase orders
 * - resultCount: Total count of results
 * - Query status information (error, refetch, etc.)
 *
 * @throws Will suspend while loading data
 * @throws Will throw errors from the API if request fails
 */
export function usePurchaseOrders(options: UsePurchaseOrdersOptions = {}) {
  // Set up React Query with suspense mode
  const { data, ...query } = useSuspenseQuery({
    queryKey: purchaseOrderListQueryKey(options.filter),
    queryFn: () => getPurchaseOrders(options.filter),
    refetchOnWindowFocus: true,
  });

  const purchaseOrders = data.purchaseOrders;
  const resultCount = data.resultCount;
  return { purchaseOrders, resultCount, ...query };
}

/**
 * Sort property options for purchase orders
 */
const purchaseOrderSortPropertySchema = z.enum(['poNumber', 'status']);

export type PurchaseOrderSortProperty = z.infer<
  typeof purchaseOrderSortPropertySchema
>;

/**
 * Search schema for purchase order listing page.
 */
export const purchaseOrderSearchListingSchema = z.object({
  query: searchTermSchema,
  status: poPhaseSchema.optional(),
  subStatuses: z.array(poSubstatusSchema).optional(),
  page: z.number().optional(),
  sort: purchaseOrderSortPropertySchema.optional(),
  direction: sortDirectionSchema.optional(),
  fromLocations: z.array(z.string()).optional(),
  fromCustomerLocations: z.array(z.string()).optional(),
  fromLocationCompanyTypes: z
    .array(locationCompanyTypeSchema)
    .optional()
    .catch([]),
  toLocations: z.array(z.string()).optional(),
  toCustomerLocations: z.array(z.string()).optional(),
  toLocationCompanyTypes: z
    .array(locationCompanyTypeSchema)
    .optional()
    .catch([]),
});

export type PurchaseOrderSearchListing = z.infer<
  typeof purchaseOrderSearchListingSchema
>;
